import React from 'react';

import { ErrorCodes, guestSessionHeaderName, possibleTypes } from '@aline/common';
import { link, routes } from '@aline/routes';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { createClient } from 'graphql-ws';
import { getGuestSessionToken, getWebsocketConnectionParams, redirectToLogin } from 'src/auth';
import { sessionTimeOutModalVar } from 'src/components/auth/SessionTimeOutModal';
import { UpgradeRequiredSnackbar } from 'src/components/snackbars/UpgradeRequiredSnackbar';
import { getClientPublicOrigin, isDev, subscriptionsEndpoint } from 'src/config';
import { typePolicies } from 'src/gql/apollo-type-policies';
import {
  getIsIgnoredOperation,
  getIsSelfReportedOperation,
  getShouldHandleError,
} from 'src/gql/error-link-policy';
import { namedOperations } from 'src/gql/generated/apollo-types';
import { redirectTo } from 'src/helpers';
import { pushNotification } from 'src/notifications';
import { getErrorCode, handleError } from 'src/store/errors';
import { v4 as uuid4 } from 'uuid';

// In development, load error messages for better debugging
// These are omitted from production bundle by default to reduce size
if (isDev) {
  loadDevMessages();
  loadErrorMessages();
}

/**
 * Tab-scoped guest sessions live in sessionStorage (not cookies), so HTTP
 * GraphQL must attach them explicitly. Prefer this over the WorkOS cookie.
 */
const guestSessionLink = new SetContextLink(({ headers }) => {
  const guestToken = getGuestSessionToken();
  if (!guestToken) return { headers };

  return {
    headers: {
      ...headers,
      [guestSessionHeaderName]: guestToken,
    },
  };
});
const uploadLink = new UploadHttpLink({
  // See https://www.apollographql.com/docs/react/networking/authentication/#cookie.
  credentials: 'same-origin',
  headers: {
    /**
     * Required because of `csrfPrevention: true` on Apollo Server.
     * @url https://www.apollographql.com/docs/apollo-server/v3/data/file-uploads/
     */
    'Apollo-Require-Preflight': 'true',
  },
  uri: `${getClientPublicOrigin()}${routes.ApiGraphQL}`,
});
// Operations whose views show their own alert for a CRM record deleted in the CRM.
const CRM_RECORD_NOT_FOUND_HANDLED_OPERATIONS: readonly string[] = [
  namedOperations.Query.GetOpportunity,
];

// Effectively an error middleware. In this case, we simply warn the error message.
// ErrorLink callback now receives { error, operation } instead of { graphQLErrors, networkError, operation }
export const handleApolloError: ConstructorParameters<typeof ErrorLink>[0] = ({
  error,
  operation,
}) => {
  // Check if this is a GraphQL error (has errors array)
  if (CombinedGraphQLErrors.is(error)) {
    const graphQLErrors = error.errors;
    const isIgnoredOperation = getIsIgnoredOperation(operation.operationName);
    const isSelfReportedOperation = getIsSelfReportedOperation(operation.operationName);
    graphQLErrors.forEach(gqlError => {
      const code = getErrorCode(gqlError);

      /**
       * Guard first, then warn. `GetRequesterDocumentAccess` fails by design for
       * anyone without access, so warning above this line logged on every probe.
       */
      if (!getShouldHandleError({ code, isIgnoredOperation, isSelfReportedOperation })) return;

      const { locations, message, path } = gqlError;
      console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}.`);

      switch (code) {
        case ErrorCodes.USER_DEACTIVATED_ERROR:
          redirectTo(link.Deactivated());
          break;
        case ErrorCodes.BAD_USER_INPUT: {
          pushNotification({
            key: uuid4(),
            message: gqlError.message,
            variant: 'error',
          });
          break;
        }
        case ErrorCodes.UNAUTHORIZED: {
          pushNotification({
            key: uuid4(),
            message: 'Not authorized to perform requested action.',
            variant: 'error',
          });
          break;
        }
        case ErrorCodes.UPGRADE_REQUIRED: {
          const id = uuid4();
          pushNotification({
            content: React.createElement(UpgradeRequiredSnackbar, {
              id,
              message: gqlError.message,
            }),
            key: id,
            message: '',
            variant: 'warning',
          });
          break;
        }
        case ErrorCodes.CRM_RECORD_NOT_FOUND:
          if (!CRM_RECORD_NOT_FOUND_HANDLED_OPERATIONS.includes(operation.operationName ?? ''))
            pushNotification({ key: uuid4(), message: gqlError.message, variant: 'warning' });
          break;
        /**
         * A missing record is an answer, not a failure: the surface that asked
         * renders its own empty or not-found state, so neither the generic
         * snackbar nor a Sentry report adds anything.
         */
        case ErrorCodes.NOT_FOUND:
          break;
        case ErrorCodes.UNAUTHENTICATED:
          return redirectToLogin();
        default:
          return handleError(gqlError);
      }
    });
  } else {
    // Network error (not a GraphQL error)
    const networkError = error;
    if (
      networkError &&
      'statusCode' in networkError &&
      (networkError as { statusCode?: number }).statusCode === 401
    ) {
      sessionTimeOutModalVar(true);
      return;
    }

    console.warn(`[Network error]: ${networkError}.`);
    pushNotification({
      key: uuid4(),
      message: 'A network error occurred. Try your action again.',
      variant: 'error',
    });
  }
};

const errorLink = new ErrorLink(handleApolloError);

/**
 * Custom link to inject fresh auth cookies into subscription extensions.
 * This ensures each subscription uses the current cookie from the browser,
 * not the stale cookie from the initial WebSocket handshake.
 */
const wsAuthLink = new ApolloLink((operation, forward) => {
  const auth = getWebsocketConnectionParams();

  // Add auth to the operation's extensions (sent in each GraphQL payload).
  operation.extensions = {
    ...operation.extensions,
    auth,
  };

  return forward(operation);
});

const wsLink = new GraphQLWsLink(
  createClient({
    lazy: true,
    retryAttempts: 10,
    shouldRetry: () => {
      // Retry as long as we have valid auth cookies.
      const auth = getWebsocketConnectionParams();
      return Boolean(auth);
    },
    url: subscriptionsEndpoint,
  })
);

// Use websocket link only for subscriptions, everything else goes through http/the upload link.
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  ApolloLink.from([wsAuthLink, wsLink]),
  uploadLink
);

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes,
    typePolicies,
  }),

  defaultOptions: {
    // Retains all error information.
    mutate: { errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
  },
  link: ApolloLink.from([guestSessionLink, errorLink, splitLink]),
});
